import {
  BarChart2,
  Zap,
  TrendingUp,
  CheckCircle,
  Search,
  FileText,
  Compass,
  FolderCheck,
  PieChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

export function CorporateHeroSection() {
  const [fadeIn, setFadeIn] = useState(false);
  useEffect(() => {
    setFadeIn(true);
  }, []);

  return (
    <section
      className="relative w-full min-h-[540px] flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: "#134e3a" }}
    >
      {/* Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="/videos/cora-ai-brain.webm" type="video/webm" />
      </video>
      {/* Overlay for darkening and brand color */}
      <div className="absolute inset-0 bg-[#134e3a] opacity-80 z-10" />
      {/* Content */}
      <div
        className={`relative z-20 max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-10 py-16 px-4 md:py-24 md:px-0 transition-opacity duration-1000 ${
          fadeIn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
        style={{ transitionProperty: "opacity, transform" }}
      >
        {/* Left: Main Content */}
        <div className="flex-1 text-center md:text-left">
          {/* Highlighted Tagline */}
          <div className="mb-4">
            <span className="inline-block bg-green-600 text-white text-base md:text-lg font-bold px-5 py-2 rounded-full tracking-wide shadow-lg animate-fadeInUp">
              Meet CORAA: Your Corporate Impact Copilot
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
            The AI Intelligence Engine for Corporate Impact
          </h1>
          <p className="text-lg md:text-xl text-gray-100 mb-8">
            Trained on over 10 million+ data points across 2,000+ companies,
            CORAA helps you benchmark smarter, analyze faster, and align every
            impact initiative with confidence.
          </p>

          {/* Stats Block */}
          <div className="flex flex-col sm:flex-row gap-6 mb-8 justify-center md:justify-start">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-400 h-7 w-7" />
              <div>
                <div className="font-bold text-xl text-white">2,000+</div>
                <div className="text-gray-200 text-sm">
                  Corporate Reports Analyzed
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-400 h-7 w-7" />
              <div>
                <div className="font-bold text-xl text-white">10M+</div>
                <div className="text-gray-200 text-sm">
                  Real-World CSR, ESG, & Volunteering Data Points
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-400 h-7 w-7" />
              <div>
                <div className="font-bold text-xl text-white">Faster</div>
                <div className="text-gray-200 text-sm">
                  Policy & Partner Insights
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-400 h-7 w-7" />
              <div>
                <div className="font-bold text-xl text-white">5x</div>
                <div className="text-gray-200 text-sm">
                  More Strategic Decisions Enabled
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button with animated glow on hover */}
          <Button
            size="lg"
            className="bg-primary text-white px-8 py-3 rounded-full shadow-lg hover:bg-primary-hover transition-all duration-300 relative group overflow-hidden"
            style={{ boxShadow: "0 4px 24px 0 rgba(34,197,94,0.25)" }}
          >
            <span className="relative z-10">Try CORAA for Free</span>
            <span className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-80 group-hover:scale-110 transition-all duration-300 bg-green-400 blur-xl" />
          </Button>
        </div>

        {/* Right: Feature Bullets */}
        <div className="flex-1 flex flex-col gap-6 max-w-md mx-auto md:mx-0">
          <div className="flex items-start gap-3">
            <Compass className="text-green-400 h-7 w-7 mt-1" />
            <div>
              <div className="font-bold text-white text-lg">
                Instant Program Benchmarking
              </div>
              <div className="text-gray-200 text-sm">
                Compare goals, spend, themes, and outcomes across 2,000+ peer
                organizations.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Search className="text-blue-300 h-7 w-7 mt-1" />
            <div>
              <div className="font-bold text-white text-lg">
                Smart NPO Discovery & Partner Insights
              </div>
              <div className="text-gray-200 text-sm">
                Find credible nonprofits by theme, region, compliance, or
                previous partnerships.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <FileText className="text-yellow-400 h-7 w-7 mt-1" />
            <div>
              <div className="font-bold text-white text-lg">
                Document-Based Risk & Credibility Checks
              </div>
              <div className="text-gray-200 text-sm">
                Auto-verify CSR registration numbers, certifications, and
                disclosure alignment.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <PieChart className="text-pink-400 h-7 w-7 mt-1" />
            <div>
              <div className="font-bold text-white text-lg">
                AI-Powered Goal & Policy Comparison
              </div>
              <div className="text-gray-200 text-sm">
                Understand how top companies structure goals, track milestones,
                and disclose impact , instantly.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
